/**
 * ScenarioComparison — overlay chart + side-by-side table + diff + sorting.
 *
 * Enhancement 1: Grouped BarChart showing currency metrics per scenario
 * Enhancement 2: Delta column (current vs each saved scenario)
 * Enhancement 3: Sortable metric rows (click header to rank scenarios)
 */

import { useState, useMemo } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import { CHART_COLORS } from '../../config/chartTheme'
import type { ProjectionResult } from '../../services/projectionEngine'

interface SavedScenario {
  id: number
  name: string
  computed_outputs: ProjectionResult | null
}

interface ScenarioComparisonProps {
  scenarios: SavedScenario[]
  currentProjection: ProjectionResult | null
  className?: string
}

interface ComparisonRow {
  key: string
  label: string
  getValue: (p: ProjectionResult) => number
  format: (v: number) => string
  higherIsBetter: boolean
  isChartable: boolean          // currency metrics go in bar chart
}

const ROWS: ComparisonRow[] = [
  { key: 'revenue',       label: 'Revenue',       getValue: p => p.pl.revenue,          format: fmtDollar, higherIsBetter: true,  isChartable: true },
  { key: 'cogs',          label: 'COGS',          getValue: p => p.pl.cogs,             format: fmtDollar, higherIsBetter: false, isChartable: true },
  { key: 'net_income',    label: 'Net Income',    getValue: p => p.pl.net_income,       format: fmtDollar, higherIsBetter: true,  isChartable: true },
  { key: 'ending_cash',   label: 'Ending Cash',   getValue: p => p.cf.ending_cash,      format: fmtDollar, higherIsBetter: true,  isChartable: true },
  { key: 'dso',           label: 'DSO',           getValue: p => p.kpis.dso,            format: v => `${v} days`,         higherIsBetter: false, isChartable: false },
  { key: 'current_ratio', label: 'Current Ratio', getValue: p => p.kpis.current_ratio,  format: v => `${v.toFixed(2)}x`,  higherIsBetter: true,  isChartable: false },
  { key: 'gross_margin',  label: 'Gross Margin',  getValue: p => p.kpis.gross_margin,   format: v => `${v.toFixed(1)}%`,  higherIsBetter: true,  isChartable: false },
  { key: 'net_margin',    label: 'Net Margin',    getValue: p => p.kpis.net_margin,     format: v => `${v.toFixed(1)}%`,  higherIsBetter: true,  isChartable: false },
]

const BAR_COLORS = CHART_COLORS.series.slice(0, 8)

function fmtDollar(v: number): string {
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`
  return `${sign}$${abs.toFixed(0)}`
}

function fmtDelta(v: number, format: (v: number) => string): string {
  const prefix = v > 0 ? '+' : ''
  return prefix + format(v)
}

export function ScenarioComparison({ scenarios, currentProjection, className }: ScenarioComparisonProps) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  // Build columns: saved scenarios + current
  const columns = useMemo(() => {
    const cols: { name: string; projection: ProjectionResult; isCurrent: boolean }[] = []
    scenarios.forEach(s => {
      if (s.computed_outputs) {
        cols.push({ name: s.name, projection: s.computed_outputs, isCurrent: false })
      }
    })
    if (currentProjection) {
      cols.push({ name: 'Current', projection: currentProjection, isCurrent: true })
    }
    return cols
  }, [scenarios, currentProjection])

  // Sort columns by selected metric
  const sortedColumns = useMemo(() => {
    if (!sortKey) return columns
    const row = ROWS.find(r => r.key === sortKey)
    if (!row) return columns
    return [...columns].sort((a, b) => {
      const aVal = row.getValue(a.projection)
      const bVal = row.getValue(b.projection)
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [columns, sortKey, sortDir])

  // Chart data: only chartable (currency) metrics
  const chartData = useMemo(() => {
    if (columns.length === 0) return []
    return ROWS
      .filter(r => r.isChartable)
      .map(row => {
        const point: Record<string, any> = { metric: row.label }
        columns.forEach(col => {
          point[col.name] = row.getValue(col.projection)
        })
        return point
      })
  }, [columns])

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(key)
      const row = ROWS.find(r => r.key === key)
      setSortDir(row?.higherIsBetter ? 'desc' : 'asc')
    }
  }

  if (columns.length === 0) {
    return (
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
        'p-8 text-center',
        className,
      )}>
        <p className="text-[13px] text-[var(--text-muted,#64748B)]">
          Save your first scenario to compare
        </p>
        <p className="text-[11px] text-[var(--text-hint,#475569)] mt-1">
          Adjust assumptions and click Save to create a scenario
        </p>
      </div>
    )
  }

  // Find the "Current" column for delta calculation
  const currentCol = columns.find(c => c.isCurrent)
  const showDelta = !!currentCol && columns.length >= 2

  return (
    <div className={className}>
      <h4 className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-3 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
        Scenario Comparison
      </h4>

      {/* Enhancement 1: Overlay Bar Chart */}
      {chartData.length > 0 && columns.length >= 2 && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
          'p-4 mb-3',
        )}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
              <XAxis
                dataKey="metric"
                tick={{ fill: 'var(--text-muted, #64748B)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'var(--text-muted, #64748B)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => fmtDollar(v)}
                width={60}
              />
              <Tooltip
                formatter={(value) => fmtDollar(Number(value))}
                contentStyle={{
                  backgroundColor: 'var(--bg-elevated, var(--bg-elevated))',
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--text-primary, #F0F2F5)',
                }}
                labelStyle={{
                  color: 'var(--text-secondary, #94A3B8)',
                  fontSize: '11px',
                  marginBottom: '4px',
                }}
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px', color: 'var(--text-muted, #64748B)' }}
              />
              {columns.map((col, i) => (
                <Bar
                  key={col.name}
                  dataKey={col.name}
                  fill={BAR_COLORS[i % BAR_COLORS.length]}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={40}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table with sorting + delta */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
        'overflow-x-auto',
      )}>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left px-3 py-2 text-[var(--text-muted,#64748B)] font-medium">
                Metric
              </th>
              {sortedColumns.map((col, i) => (
                <th key={i} className="text-right px-3 py-2 text-[var(--text-muted,#64748B)] font-medium">
                  <span className={cn(
                    'inline-flex items-center gap-1',
                    col.isCurrent && 'text-[var(--accent,var(--accent))]',
                  )}>
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: BAR_COLORS[columns.indexOf(col) % BAR_COLORS.length] }} />
                    {col.name}
                  </span>
                </th>
              ))}
              {showDelta && (
                <th className="text-right px-3 py-2 text-[var(--text-muted,#64748B)] font-medium whitespace-nowrap">
                  vs Current
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => {
              const values = sortedColumns.map(col => row.getValue(col.projection))
              const best = row.higherIsBetter ? Math.max(...values) : Math.min(...values)
              const worst = row.higherIsBetter ? Math.min(...values) : Math.max(...values)
              const currentVal = currentCol ? row.getValue(currentCol.projection) : 0

              // For delta: compare first non-current column vs current
              const firstSaved = sortedColumns.find(c => !c.isCurrent)
              const deltaVal = firstSaved ? row.getValue(firstSaved.projection) - currentVal : 0
              const deltaPositive = row.higherIsBetter ? deltaVal > 0 : deltaVal < 0

              return (
                <tr key={row.key} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="px-3 py-1.5">
                    <button
                      onClick={() => handleSort(row.key)}
                      className={cn(
                        'flex items-center gap-1 text-[var(--text-secondary,#94A3B8)] hover:text-[var(--text-primary,#F0F2F5)] transition-colors',
                        sortKey === row.key && 'text-[var(--accent,var(--accent))]',
                      )}
                    >
                      {row.label}
                      {sortKey === row.key ? (
                        sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-2.5 w-2.5 opacity-30" />
                      )}
                    </button>
                  </td>
                  {sortedColumns.map((col, i) => {
                    const v = values[i]
                    const isBest = sortedColumns.length > 1 && v === best
                    const isWorst = sortedColumns.length > 1 && v === worst
                    return (
                      <td
                        key={i}
                        className={cn(
                          'px-3 py-1.5 text-right font-mono text-[var(--text-primary,#F0F2F5)]',
                          isBest && 'bg-emerald-500/5',
                          isWorst && 'bg-red-500/5',
                        )}
                      >
                        {row.format(v)}
                      </td>
                    )
                  })}
                  {showDelta && (
                    <td className={cn(
                      'px-3 py-1.5 text-right font-mono whitespace-nowrap',
                      deltaVal === 0 && 'text-[var(--text-muted,#64748B)]',
                      deltaVal !== 0 && deltaPositive && 'text-emerald-400',
                      deltaVal !== 0 && !deltaPositive && 'text-red-400',
                    )}>
                      {deltaVal === 0 ? '--' : (
                        <>
                          {fmtDelta(deltaVal, row.format)}
                          {' '}
                          {deltaPositive ? '▲' : '▼'}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
